import { createTheme } from '@abyss/web/tools/theme';
import { OverlayProvider } from '@abyss/web/ui/OverlayProvider';
import { RouterProvider } from '@abyss/web/ui/RouterProvider';
import { ThemeProvider } from '@abyss/web/ui/ThemeProvider';
import React, { useContext } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { ReverseCoverageTypesCodes } from '../../common/Constants';
import { DetailsWidgetView } from '../../common/Widget/ProviderDetails';
import {
  PortalContext,
  PortalContextProvider,
} from '../../context/PortalContext';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { coerceBoolean } from '../../utils/string.utils';
import { Configuration } from '../ProviderSearch/context/Configuration';
import { DateTimeConfig } from '../ProviderSearch/context/DateTimeConfig';
import { Internationalization } from '../ProviderSearch/context/Internationalization';
import { Obapi } from '../ProviderSearch/context/Obapi';
import { PlanSelector } from '../ProviderSearch/context/PlanSelector';
import { getTheme } from '../themeOverrides/theme.utils';

interface ProviderDetailsWidgetAttrs {
  'claim-system-type-code'?: string;
  'coverage-type'?: string;
  'ddp-code'?: string;
  'eligibility-system-type-code'?: string;
  latitude?: string;
  lob?: string;
  'location-id'?: string;
  longitude?: string;
  'out-of-pocket-cost'?: string;
  'plan-variation-code'?: string;
  'plan-year'?: string;
  'policy-id'?: string;
  'provider-id'?: string;
  'provider-type'?: string;
  'reciprocity-id'?: string;
  'total-cost'?: string;
  'membership-category'?: string;
  population?: string;
  'portal-data'?: string;
  'is-smart-choice-enabled'?: string;
  'client-name'?: string;
  'is-super-user'?: string | boolean;
  'billing-codes'?: string;
  'recommendation-radius'?: string;
  'tiered-plan'?: string;
  'suppress-premium'?: string;
  'ces-product-code'?: string;
  'market-type'?: string;
  'rules-package-key'?: string;
}

/**
 * Provider Details Widget entry point.
 *
 * If CEX passes full member info in `portal-data`, use it.
 * Otherwise, build the smallest member object needed for `acoBenefitPlan`.
 *
 * @param args - Widget attributes from HTML element
 */
export const ProviderDetailsWidget = (args: ProviderDetailsWidgetAttrs) => {
  const {
    'claim-system-type-code': claimSystemTypeCode,
    'coverage-type': coverageType,
    'ddp-code': ddpCode,
    'eligibility-system-type-code': eligibilitySystemTypeCode,
    latitude,
    lob,
    'location-id': locationId,
    longitude,
    'out-of-pocket-cost': outOfPocketCost,
    'plan-variation-code': planVariationCode,
    'plan-year': planYear,
    'policy-id': policyId,
    'provider-id': providerId,
    'provider-type': providerType,
    'reciprocity-id': reciprocityId,
    'total-cost': totalCost,
    'membership-category': membershipCategory,
    population,
    'portal-data': portalData,
    'is-smart-choice-enabled': isSmartChoiceEnabled,
    'client-name': clientName,
    'is-super-user': isSuperUserRaw,
    'billing-codes': billingCodes,
    'recommendation-radius': recommendationRadius,
    'tiered-plan': tieredPlan,
    'suppress-premium': suppressPremium,
    'ces-product-code': cesProductCode,
    'market-type': marketType,
    'rules-package-key': rulesPackageKey,
  } = args || {};

  const parsedPortalData = {
    ...JSON.parse(portalData || '{}'),
    clientName,
    recommendationRadius,
    tieredPlan,
    suppressPremium,
  };

  const isSuperUser = coerceBoolean(isSuperUserRaw);

  const { setPortalData } = useContext(PortalContext);
  const isPortalWidget = parsedPortalData?.portalName;
  const isGEHAV2: boolean = shouldUseGehaV2Styling();

  const themeOverride = getTheme(
    parsedPortalData?.portalConfig?.theme,
    parsedPortalData?.portalIdentifier,
    parsedPortalData?.portalConfig?.themeOverride,
    isGEHAV2
  );

  const theme = createTheme(
    parsedPortalData?.portalConfig?.theme || 'uhc',
    themeOverride
  );

  const setIsWidget = useStore(StoreKeys.SET_IS_WIDGET);
  const setUserDdpCode = useStore(StoreKeys.SET_USER_DDP_CODE);
  const setMemberState = useStore(StoreKeys.SET_OBAPI_MEMBERS);

  const { setStoreCoverageType } = useChipStore(
    useShallow((state: ChipState) => ({
      setStoreCoverageType: state.setCoverageType,
    }))
  );

  setIsWidget(true);
  setPortalData(parsedPortalData);
  // Use the same default as the rendered widget to keep chip store consistent.
  setStoreCoverageType(coverageType || 'M');
  setUserDdpCode(ddpCode);
  if (isPortalWidget) {
    const portalMemberInfo = parsedPortalData?.portalMemberInfo?.memberInfo;
    if (portalMemberInfo?.length) {
      setMemberState(portalMemberInfo);
    } else {
      // Build the minimum member shape needed for acoBenefitPlan.
      // Include productCode / marketType from widget attrs so getAcoBenefitPlan()
      // can match against ACO_BENEFIT_COLLECTION even without portalMemberInfo.
      const widgetMemberInfo = [
        {
          eligibility: [
            {
              memberHealthCoverage: {
                coverageType: [
                  {
                    typeCode: { coverageTypeCode: coverageType || 'M' },
                    ...(cesProductCode ? { productCode: cesProductCode } : {}),
                    ...(marketType ? { marketType } : {}),
                  },
                ],
              },
            },
          ],
          lineOfBusiness: lob,
          membershipCategory,
          policyNumber: policyId,
          population,
        },
      ];
      setMemberState(widgetMemberInfo);
    }
  }

  const adjustedReciprocityId =
    coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] &&
    reciprocityId === '0'
      ? 'Employer%20Guest'
      : reciprocityId;

  const ProviderDetailsDrawerWidget = () => (
    <Internationalization>
      <OverlayProvider>
        <ThemeProvider theme={theme}>
          {providerId ? (
            <DetailsWidgetView
              billingCodes={billingCodes}
              cesProductCode={cesProductCode}
              claimSystemTypeCode={claimSystemTypeCode}
              coverageType={coverageType}
              ddpUserCode={ddpCode}
              eligibilitySystemTypeCode={eligibilitySystemTypeCode}
              isSmartChoiceEnabled={JSON.parse(isSmartChoiceEnabled)}
              isSuperUser={isSuperUser}
              latitude={latitude}
              lob={lob}
              locationId={locationId}
              longitude={longitude}
              memberMarketType={marketType}
              membershipCategory={membershipCategory}
              outOfPocketCost={outOfPocketCost}
              planVariationCode={planVariationCode}
              planYear={planYear}
              policyId={policyId}
              population={population}
              providerId={providerId}
              providerType={providerType || 'Practitioner'}
              reciprocityId={adjustedReciprocityId}
              rulesPackageKey={rulesPackageKey}
              totalCost={totalCost}
            />
          ) : (
            <div>No Provider ID</div>
          )}
        </ThemeProvider>
      </OverlayProvider>
    </Internationalization>
  );

  if (isPortalWidget) {
    return (
      <RouterProvider>
        <PortalContextProvider portalDataArgs={parsedPortalData}>
          <Configuration>
            <PlanSelector>
              <DateTimeConfig>
                <ProviderDetailsDrawerWidget />
              </DateTimeConfig>
            </PlanSelector>
          </Configuration>
        </PortalContextProvider>
      </RouterProvider>
    );
  }
  return (
    <RouterProvider>
      <Configuration>
        <PlanSelector>
          <DateTimeConfig>
            <Obapi>
              <ProviderDetailsDrawerWidget />
            </Obapi>
          </DateTimeConfig>
        </PlanSelector>
      </Configuration>
    </RouterProvider>
  );
};
